import type { LucideIcon } from "lucide-react";

export type AbaNavbar = { key: string; label: string; icon: LucideIcon; path: string };

/**
 * A navbar flutuante em pílula — a única do app.
 *
 * Referência: Dribbble 26436710 / 27589455. Fica 12px acima da borda (mais a
 * área segura do iPhone), 64px de altura; 12 + 64 = 76 = --nav-baixo, a mesma
 * reserva de padding que toda tela faz no rodapé. COM rótulo: ícone sozinho é
 * o que esse público não lê.
 *
 * O ativo é UMA pílula de ouro que desliza de uma aba para a outra: forma e
 * movimento, não só cor, para quem enxerga mal. Ela é um elemento só, atrás
 * dos botões, que anda com transform — por isso a troca de aba anima em vez
 * de piscar.
 *
 * Esta peça é só o desenho. Quem decide as abas e qual está ativa é o
 * BottomNav (app inteiro) ou o CleanCakesBottomNav (namespace do produto).
 */
export function NavbarFlutuante({
  abas,
  ativa,
  aoEscolher,
}: {
  abas: AbaNavbar[];
  /** Índice da aba ativa, ou -1 quando nenhuma. */
  ativa: number;
  aoEscolher: (aba: AbaNavbar) => void;
}) {
  const n = abas.length;
  return (
    <nav className="navbar-fixa fixed bottom-0 left-0 right-0 z-50 safe-bottom pointer-events-none">
      <div className="mx-auto max-w-[520px]" style={{ padding: "0 12px 12px" }}>
        <div
          className="pointer-events-auto relative flex items-stretch rounded-full border border-border"
          style={{
            height: 64,
            padding: 6,
            backgroundColor: "hsl(var(--card) / 0.9)",
            backdropFilter: "blur(18px) saturate(1.3)",
            WebkitBackdropFilter: "blur(18px) saturate(1.3)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.32)",
          }}
        >
          {/* a pílula de ouro: um elemento só, que desliza */}
          <span
            aria-hidden="true"
            className="absolute rounded-full"
            style={{
              top: 6,
              bottom: 6,
              left: 6,
              width: `calc((100% - 12px) / ${n})`,
              background: "hsl(var(--accent))",
              transform: `translateX(${Math.max(ativa, 0) * 100}%)`,
              opacity: ativa < 0 ? 0 : 1,
              transition: "transform 280ms cubic-bezier(.2,.8,.2,1), opacity 160ms",
            }}
          />
          {abas.map((aba, i) => {
            const Icon = aba.icon;
            const ativo = i === ativa;
            return (
              <button
                key={aba.key}
                data-onboarding={aba.key}
                type="button"
                onClick={() => aoEscolher(aba)}
                aria-current={ativo ? "page" : undefined}
                className="relative flex flex-1 flex-col items-center justify-center rounded-full"
                style={{
                  gap: 2,
                  color: ativo ? "hsl(var(--accent-foreground))" : "var(--tinta-apoio)",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  transition: "color 200ms",
                }}
              >
                <Icon size={24} strokeWidth={ativo ? 2.4 : 2} />
                <span style={{ fontSize: "var(--t-nav)", lineHeight: 1.15, fontWeight: ativo ? 700 : 600 }}>
                  {aba.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
